import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Switch,
  FormGroup,
  Checkbox,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  AccountCircle as AccountCircleIcon,
  SmartToy as SmartToyIcon,
  Star,
} from "@mui/icons-material";
import { getAccountsByPlatform } from "../../services/socialMediaAccountsService";
import axios from "axios";
import { API_BASE_URL } from "../../services/api";

const LinkedInKeywords = () => {
  // State for keywords data
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for LinkedIn accounts
  const [linkedinAccounts, setLinkedinAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [fillterationPrompt, setFillterationPrompt] = useState("");
  const [selectedKeywordPrompt, setSelectedKeywordPrompt] = useState(null);
  const [isFilteringEnabled, setIsFilteringEnabled] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    excludePromotional: true,
    excludeWithLinks: false,
    enableCustomRule: false,
    customRuleText: "",
  });

  // State for the upgrade prompt dialog
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  // State for prompt management
  const [promptManagementOpen, setPromptManagementOpen] = useState(false);
  const [keywordForPrompt, setKeywordForPrompt] = useState(null);
  // State to manage loading of specific prompt details in the dialog
  const [loadingPromptDetails, setLoadingPromptDetails] = useState(false);
  const [availableModels, setAvailableModels] = useState([
    { id: "gemini-2.5-flash", name: "gemini-2.5-flash", selected: true },
    { id: "gemini-2.5-flash-lite", name: "gemini-2.5-flash-lite", selected: false },
    { id: " gemini-2.5-pro", name: "  gemini-2.5-pro", selected: false },
  ]);
  const [selectedModel, setSelectedModel] = useState("meta-llama/llama-3-8b-instruct");

  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState("default");
  const [customPrompt, setCustomPrompt] = useState("");
  const [isCustomPrompt, setIsCustomPrompt] = useState(false);
  const [editedPreviewContent, setEditedPreviewContent] = useState("");
  const [isPreviewEdited, setIsPreviewEdited] = useState(false);

  // State for form
  const [formOpen, setFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentKeyword, setCurrentKeyword] = useState({
    id: null,
    text: "",
    minConnections: 0,
    accountId: null,
    promptId: null,
  });

  // State for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keywordToDelete, setKeywordToDelete] = useState(null);

  // State for filtering
  const [filters, setFilters] = useState({
    text: "",
    minConnections: 0,
    accountId: null,
  });
  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const COLS = {
    keyword: "42%",
    account: "28%",
    minConn: "15%",
    actions: "15%",
  };

  useEffect(() => {
    fetchKeywords();
    fetchLinkedInAccounts();
    fetchAllPrompts();

    const promptRefreshInterval = setInterval(() => {
      console.log("Refreshing prompts from backend...");
      fetchAllPrompts();
    }, 30000);

    return () => clearInterval(promptRefreshInterval);
  }, []);

  const setDefaultPromptForKeyword = () => {
    if (availablePrompts.length > 0) {
      const defaultPrompt = availablePrompts.find((p) => p.id === "default") || availablePrompts[0];
      setSelectedPrompt(defaultPrompt.id);
      setEditedPreviewContent(defaultPrompt.content);
      setIsPreviewEdited(false);

      if (defaultPrompt.model) {
        setSelectedModel(defaultPrompt.model);
      }

      return defaultPrompt.id;
    }
    return null;
  };

  const fetchAllPrompts = () => {
    const defaultPrompts = [
      {
        id: "default",
        name: "Default",
        content: `Reply smartly to this LinkedIn post:\n"{postContent}".\nMake it professional, engaging, and relevant. Be business-appropriate and provide value to the conversation.`,
        model: "gemini-2.5-flash",
      },
      {
        id: "professional",
        name: "Professional",
        content: `Craft a professional response to this LinkedIn post:\n"{postContent}".\nUse formal language, be concise, and maintain a business-appropriate tone.`,
        model: "gemini-2.5-flash",
      },
      {
        id: "engaging",
        name: "Engaging",
        content: `Create an engaging reply to this LinkedIn post:\n"{postContent}".\nAsk a thoughtful question to encourage further conversation while being relevant to the original content.`,
        model: "gemini-2.5-flash",
      },
      {
        id: "supportive",
        name: "Supportive",
        content: `Write a supportive response to this LinkedIn post:\n"{postContent}".\nShow empathy, offer encouragement, and be positive while keeping it brief and genuine.`,
        model: "gemini-2.5-flash",
      },
    ];

    setAvailablePrompts(defaultPrompts);
    console.log(`Using ${defaultPrompts.length} default prompts for LinkedIn`);
    return true;
  };

  useEffect(() => {
    fetchKeywords();
  }, [selectedAccount]);

  const fetchLinkedInAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const token = localStorage.getItem("token");
      const response = await getAccountsByPlatform("linkedin", token);
      setLinkedinAccounts(response.data || []);
    } catch (err) {
      console.error("Error fetching LinkedIn accounts:", err);
      setLinkedinAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchKeywords = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const params = selectedAccount ? `?accountId=${selectedAccount}` : "";
      const response = await axios.get(`${API_BASE_URL}/linkedin-keywords${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setKeywords(response.data.data || []);
      console.log(response.data.data, "keywords");
    } catch (err) {
      setError("Failed to fetch LinkedIn keywords. Please try again later.");
      console.error("Error fetching LinkedIn keywords:", err);
      setKeywords([]);
    } finally {
      setLoading(false);
    }
  };

  // New handler to open the prompt dialog and fetch keyword-specific settings
  const handleManagePromptClick = async (keyword) => {
    setKeywordForPrompt(keyword);
    setPromptManagementOpen(true);
    setLoadingPromptDetails(true);

    // Reset states to default to avoid showing stale data from a previous keyword
    setIsFilteringEnabled(false);
    setFilterOptions({
      excludePromotional: true,
      excludeWithLinks: false,
      enableCustomRule: false,
      customRuleText: "",
    });
    setDefaultPromptForKeyword(); // Set a default reply prompt while fetching

    try {
      const token = localStorage.getItem("token");
      // This is the new API call. Ensure this endpoint exists on your backend.
      // It should return the prompt object associated with the keywordId.
      const response = await axios.get(`${API_BASE_URL}/prompts/keyword/${keyword.text}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const promptData = response.data.data;
      console.log("promtData", promptData);

      // If data is returned, update the state to reflect saved settings
      if (promptData) {
        setSelectedModel(promptData.model || "gemini-2.5-flash");
        setEditedPreviewContent(promptData.content || "");
        setIsPreviewEdited(true); // Mark as custom since we're loading it directly

        const isFiltering = promptData.isaifilteringenabled ?? false;
        console.log(  "Is AI Filtering Enabled for this keyword?", isFiltering);
        setIsFilteringEnabled(isFiltering);

        // If filtering is enabled, parse the saved prompt to restore the UI
        if (isFiltering && promptData.fillterationPrompt) {
          // NOTE: This parsing is dependent on the exact string constructed during save.
          // A more robust solution is to store filter options as a JSON object in the database.
          const newFilterOptions = {
            excludePromotional: promptData.fillterationPrompt.includes("not be promotional"),
            excludeWithLinks: promptData.fillterationPrompt.includes("not contain any URLs or external links"),
            enableCustomRule: false,
            customRuleText: "",
          };

          const rulesPrefix = "You must follow these rules: ";
          const rulesSuffix = "Based on these rules";
          const rulesStartIndex = promptData.fillterationPrompt.indexOf(rulesPrefix);
          const rulesEndIndex = promptData.fillterationPrompt.indexOf(rulesSuffix);

          if (rulesStartIndex !== -1 && rulesEndIndex !== -1) {
            let allRulesText = promptData.fillterationPrompt
              .substring(rulesStartIndex + rulesPrefix.length, rulesEndIndex)
              .trim();

            allRulesText = allRulesText.replace("the post must not be promotional, an ad, or a sales pitch.", "").trim();
            allRulesText = allRulesText.replace("the post must not contain any URLs or external links.", "").trim();

            if (allRulesText) {
              newFilterOptions.enableCustomRule = true;
              newFilterOptions.customRuleText = allRulesText;
            }
          }
          setFilterOptions(newFilterOptions);
        }
      }
    } catch (error) {
      // A 404 error is expected if no prompt has been saved for this keyword yet.
      // In that case, we simply keep the default state that was set at the start.
      if (error.response && error.response.status === 404) {
        console.log("No specific prompt found for this keyword. Using defaults.");
      } else {
        console.error("Error fetching prompt settings:", error);
        setNotification({
          open: true,
          message: "Could not load AI settings for this keyword.",
          severity: "error",
        });
        setPromptManagementOpen(false); // Close the dialog on a critical error
      }
    } finally {
      setLoadingPromptDetails(false); // Hide the loader
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const filtersWithAccount = {
        ...filters,
        accountId: selectedAccount || filters.accountId,
      };

      const queryParams = new URLSearchParams();
      Object.keys(filtersWithAccount).forEach((key) => {
        if (filtersWithAccount[key]) {
          queryParams.append(key, filtersWithAccount[key]);
        }
      });

      const response = await axios.get(`${API_BASE_URL}/linkedin-keywords/filter?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setKeywords(response.data.data || []);
    } catch (err) {
      setError("Failed to apply filters. Please try again later.");
      console.error("Error applying filters:", err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      text: "",
      minLikes: "",
      minComments: "",
      minConnections: "",
      accountId: "",
    });
    fetchKeywords();
  };

  const handleAccountChange = (accountId) => {
    setSelectedAccount(accountId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentKeyword({
      ...currentKeyword,
      [name]: name === "text" || name === "accountId" ? value : Number(value),
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleAddKeyword = () => {
    setCurrentKeyword({
      id: null,
      text: "",
      minLikes: 0,
      minComments: 0,
      minConnections: 0,
      accountId: selectedAccount,
    });
    setIsEditing(false);
    setFormOpen(true);
  };

  const handleEditKeyword = (keyword) => {
    setCurrentKeyword({
      id: keyword.id,
      text: keyword.text,
      minConnections: keyword.minConnections,
      accountId: keyword.accountId,
      promptId: null,
    });
    setIsEditing(true);
    setFormOpen(true);

    const promptId = setDefaultPromptForKeyword();
    setSelectedKeywordPrompt(promptId);
  };

  const handleDeleteClick = (id) => {
    setKeywordToDelete(id);
    setDeleteDialogOpen(true);
  };

  const sendPrompt = async () => {
    try {
      const response = await axios.post("/api/search/linkedinPrompt", {
        keyword: "AI",
        prompt: fillterationPrompt,
      });

      console.log("✅ Prompt sent and saved:", response.data);
    } catch (error) {
      console.error("❌ Error sending prompt:", error);
    }
  };

  const handleSubmit = async () => {
    if (!currentKeyword.text.trim()) {
      setNotification({
        open: true,
        message: "Keyword text is required",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const keywordTexts = currentKeyword.text.split(",").map((keyword) => keyword.trim()).filter((keyword) => keyword !== "");

      if (isEditing) {
        const keywordData = {
          id: currentKeyword.id,
          text: currentKeyword.text,
          minConnections: currentKeyword.minConnections || 0,
          accountId: currentKeyword.accountId ? Number(currentKeyword.accountId) : null,
        };

        console.log("Updating LinkedIn keyword with data:", keywordData);
        await axios.put(`${API_BASE_URL}/linkedin-keywords/${currentKeyword.id}`, keywordData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        for (const keywordText of keywordTexts) {
          const keywordData = {
            text: keywordText,
            minConnections: currentKeyword.minConnections || 0,
            accountId: currentKeyword.accountId ? Number(currentKeyword.accountId) : null,
          };

          console.log("Submitting LinkedIn keyword with data:", keywordData);
          try {
            const response = await axios.post(`${API_BASE_URL}/linkedin-keywords`, keywordData, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            console.log("LinkedIn keyword added successfully:", response);
          } catch (addError) {
            console.error("Error adding LinkedIn keyword:", addError);
            throw addError;
          }
        }
      }

      await fetchKeywords();

      const keywordCount = !isEditing ? currentKeyword.text.split(",").filter((k) => k.trim() !== "").length : 1;
      const successMessage = isEditing
        ? "LinkedIn keyword updated successfully"
        : keywordCount > 1
        ? `${keywordCount} LinkedIn keywords added successfully`
        : "LinkedIn keyword added successfully";

      setNotification({
        open: true,
        message: successMessage,
        severity: "success",
      });
      setFormOpen(false);
    } catch (err) {
      setNotification({
        open: true,
        message: `Failed to ${isEditing ? "update" : "add"} LinkedIn keyword. Please try again. Error: ${
          err.message || "Unknown error"
        }`,
        severity: "error",
      });
      console.error(`Error ${isEditing ? "updating" : "adding"} LinkedIn keyword:`, err);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/linkedin-keywords/${keywordToDelete}?hard=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await fetchKeywords();

      setNotification({
        open: true,
        message: "LinkedIn keyword deleted (archived)",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: "Failed to delete LinkedIn keyword. Please try again.",
        severity: "error",
      });
      console.error("Error deleting LinkedIn keyword:", err);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
          LinkedIn Keywords Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddKeyword} sx={{ backgroundColor: "#4D99A3", border: "none" }}>
            Add Keyword
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: "0.9rem" }}>
              Select LinkedIn Account:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <FormControl fullWidth size="small">
              <Select value={selectedAccount || ""} onChange={(e) => handleAccountChange(e.target.value)} displayEmpty sx={{ minWidth: 250 }}>
                <MenuItem value="" sx={{ fontSize: "0.9rem" }}>
                  <em sx={{ fontSize: "0.9rem" }}>All Accounts</em>
                </MenuItem>
                {linkedinAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="">{account.accountName}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 0.5, mb: 3 }}>
        <Grid container spacing={0.5} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              name="text"
              label="Keyword"
              value={filters.text}
              onChange={handleFilterChange}
              fullWidth
              size="small"
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                style: { fontSize: "0.8rem" },
              }}
              InputLabelProps={{ style: { fontSize: "0.8rem" } }}
            />
          </Grid>
          <Grid item xs={6} sm={1}>
            <Tooltip title="Clear filters">
              <IconButton size="small" onClick={clearFilters}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={1.2}>
            <Button
              onClick={applyFilters}
              variant="contained"
              disabled={loading}
              startIcon={<FilterIcon sx={{ fontSize: "0.8rem" }} />}
              size="small"
              sx={{ minWidth: "28px", height: "28px", border: "none", backgroundColor: "#4D99A3", fontSize: "0.7rem", py: 0, px: 1 }}
            >
              Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        sx={{
          mb: 4,
          "& .MuiTableCell-root": { py: 1.25, px: 2 },
        }}
      >
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: COLS.keyword }}>Keyword</TableCell>
              <TableCell sx={{ width: COLS.account }}>Account</TableCell>
              <TableCell sx={{ width: COLS.actions }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : keywords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No LinkedIn keywords found. Add a new keyword to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              keywords.map((keyword) => (
                <TableRow key={keyword.id}>
                  <TableCell
                    sx={{
                      width: COLS.keyword,
                      maxWidth: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={keyword.text}
                  >
                    {keyword.text}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: COLS.account,
                      maxWidth: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={keyword.accountId ? keyword.accountName : null}
                  >
                    {keyword.accountId ? `${keyword.accountName}` : null}
                  </TableCell>
                  <TableCell sx={{ width: COLS.actions }} align="center">
                    <Box sx={{ display: "inline-flex", gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditKeyword(keyword)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteClick(keyword.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage Prompt">
                        <IconButton size="small" onClick={() => handleManagePromptClick(keyword)}>
                          <SmartToyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? "Edit LinkedIn Keyword" : "Add New LinkedIn Keyword"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="text"
                label="Keyword Text"
                value={currentKeyword.text}
                onChange={handleInputChange}
                fullWidth
                required
                helperText={!isEditing ? "You can enter multiple keywords separated by commas" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="minConnections"
                label="Minimum Connections"
                type="number"
                value={currentKeyword.minConnections}
                onChange={handleInputChange}
                fullWidth
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <FormControl sx={{ width: "50%" }} size="small">
              <InputLabel id="account-select-label">LinkedIn Account</InputLabel>
              <Select
                labelId="account-select-label"
                id="account-select"
                name="accountId"
                value={currentKeyword.accountId || ""}
                onChange={handleInputChange}
                label="LinkedIn Account"
              >
                <MenuItem value="">
                  <em>(All Accounts)</em>
                </MenuItem>
                {linkedinAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {`${account.accountName}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : isEditing ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this LinkedIn keyword? This action will soft delete the keyword.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={promptManagementOpen}
        onClose={() => {
          setPromptManagementOpen(false);
          setIsPreviewEdited(false);
          setEditedPreviewContent("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {keywordForPrompt
            ? `Manage AI Reply Prompt for LinkedIn Keyword: "${keywordForPrompt.text}"`
            : "Manage AI Reply Prompts for LinkedIn"}
        </DialogTitle>
        <DialogContent>
          {loadingPromptDetails ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Select Model
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={selectedModel}
                      onChange={(e) => {
                        const newSelectedModel = e.target.value;
                        setSelectedModel(newSelectedModel);
                        const updatedModels = availableModels.map((model) => ({
                          ...model,
                          selected: model.id === newSelectedModel,
                        }));
                        setAvailableModels(updatedModels);
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                        "& .MuiSelect-select": {
                          padding: "10px 14px",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                            "& .MuiMenuItem-root": {
                              fontSize: "0.9rem",
                              padding: "8px 16px",
                            },
                            "& .MuiMenuItem-root.Mui-selected": {
                              backgroundColor: "rgba(77, 153, 163, 0.1)",
                            },
                            "& .MuiMenuItem-root.Mui-selected:hover": {
                              backgroundColor: "rgba(77, 153, 163, 0.2)",
                            },
                          },
                        },
                      }}
                    >
                      {availableModels.map((model) => (
                        <MenuItem key={model.id} value={model.id}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <span>{model.name}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Select Prompt Template
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={isCustomPrompt ? "custom" : selectedPrompt}
                      onChange={(e) => {
                        if (e.target.value === "custom") {
                          setIsCustomPrompt(true);
                          setEditedPreviewContent(customPrompt);
                          setIsPreviewEdited(false);
                        } else {
                          setIsCustomPrompt(false);
                          setSelectedPrompt(e.target.value);
                          const selectedPromptObj = availablePrompts.find((p) => p.id === e.target.value);
                          if (selectedPromptObj) {
                            setEditedPreviewContent(selectedPromptObj.content);
                            setIsPreviewEdited(false);
                          }
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                        "& .MuiSelect-select": {
                          padding: "10px 14px",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                            "& .MuiMenuItem-root": {
                              fontSize: "0.9rem",
                              padding: "8px 16px",
                            },
                            "& .MuiMenuItem-root.Mui-selected": {
                              backgroundColor: "rgba(77, 153, 163, 0.1)",
                            },
                            "& .MuiMenuItem-root.Mui-selected:hover": {
                              backgroundColor: "rgba(77, 153, 163, 0.2)",
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, color: "#4D99A3", fontSize: "0.85rem", py: 0.5 }}>
                        Available Prompts
                      </MenuItem>
                      {availablePrompts.length > 0 ? (
                        availablePrompts.map((prompt) => (
                          <MenuItem key={prompt.id} value={prompt.id} sx={{ pl: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                              <span>{prompt.name}</span>
                            </Box>
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No prompts available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                {isCustomPrompt && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Custom Prompt
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={customPrompt}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setCustomPrompt(newValue);
                        setEditedPreviewContent(newValue);
                        setIsPreviewEdited(false);
                      }}
                      placeholder="Enter your custom prompt here. Use {postContent} as a placeholder for the LinkedIn post text."
                      helperText="Use {postContent} as a placeholder for the LinkedIn post text."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          transition: "all 0.3s",
                          fontSize: "0.95rem",
                        },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(77, 153, 163, 0.5)",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4D99A3",
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: "0.8rem",
                          marginTop: "8px",
                        },
                      }}
                    />
                  </Grid>
                )}
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Preview
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    You can edit this preview to customize the prompt
                  </Typography>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={editedPreviewContent}
                  onChange={(e) => {
                    setEditedPreviewContent(e.target.value);
                    setIsPreviewEdited(true);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "rgba(77, 153, 163, 0.04)",
                      borderColor: "rgba(77, 153, 163, 0.2)",
                    },
                    "& .MuiOutlinedInput-input": {
                      whiteSpace: "pre-wrap",
                    },
                  }}
                />
                {isPreviewEdited && (
                  <Typography variant="caption" color="primary" sx={{ display: "block", mt: 1 }}>
                    You've made custom edits to this prompt
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  AI Post Filtering
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: -1, mb: 1 }}>
                  Automatically filter out irrelevant posts using AI.
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={isFilteringEnabled}
                      onChange={(e) => setIsFilteringEnabled(e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#4D99A3",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#4D99A3",
                        },
                      }}
                    />
                  }
                  label="Enable AI Post Filtering"
                />

                {isFilteringEnabled && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Filtering Rules:
                    </Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterOptions.excludePromotional}
                            onChange={(e) =>
                              setFilterOptions({
                                ...filterOptions,
                                excludePromotional: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Exclude promotional content or ads"
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterOptions.excludeWithLinks}
                            onChange={(e) =>
                              setFilterOptions({
                                ...filterOptions,
                                excludeWithLinks: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Exclude posts that contain external links"
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterOptions.enableCustomRule}
                            onChange={(e) =>
                              setFilterOptions({
                                ...filterOptions,
                                enableCustomRule: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Add a custom filtering rule"
                      />

                      {filterOptions.enableCustomRule && (
                        <TextField
                          value={filterOptions.customRuleText}
                          onChange={(e) =>
                            setFilterOptions({
                              ...filterOptions,
                              customRuleText: e.target.value,
                            })
                          }
                          placeholder="e.g., the post must mention AI or technology"
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1, ml: 4 }}
                        />
                      )}
                    </FormGroup>
                  </Box>
                )}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPromptManagementOpen(false);
              setIsPreviewEdited(false);
              setEditedPreviewContent("");
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={async (id) => {
              try {
                let constructedFiltrationPrompt = "";
                if (isFilteringEnabled) {
                  const rules = [];
                  if (filterOptions.excludePromotional) {
                    rules.push("the post must not be promotional, an ad, or a sales pitch.");
                  }
                  if (filterOptions.excludeWithLinks) {
                    rules.push("the post must not contain any URLs or external links.");
                  }
                  if (filterOptions.enableCustomRule && filterOptions.customRuleText.trim() !== "") {
                    rules.push(filterOptions.customRuleText.trim());
                  }

                  if (rules.length > 0) {
                    constructedFiltrationPrompt = `
                      Analyze the following post content. Your task is to act as a strict content filter.
                      You must follow these rules: ${rules.join(" ")}
                      Based on these rules, decide if the post should be kept.
                      Your response must be a single word: ONLY 'true' if the post should be kept, or ONLY 'false' if it should be filtered out.
                    `;
                  }
                }

                const token = localStorage.getItem("token");
                if (!token) {
                  setNotification({
                    open: true,
                    message: "Authentication Error: Please log in again.",
                    severity: "error",
                  });
                  return;
                }
                const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
                const updatedModels = availableModels.map((model) => ({
                  ...model,
                  selected: model.id === selectedModel,
                }));
                setAvailableModels(updatedModels);
                if (isCustomPrompt) {
                  localStorage.setItem("customPromptLinkedIn", customPrompt);
                }

                let promptId;

                if (isPreviewEdited || isCustomPrompt) {
                  const promptName = keywordForPrompt
                    ? `LinkedIn Prompt for ${keywordForPrompt.text} (${new Date().toLocaleString()})`
                    : "Custom LinkedIn " + new Date().toLocaleString();
                  const promptData = {
                    name: promptName,
                    model: selectedModel,
                    content: editedPreviewContent,
                    fillterationPrompt: constructedFiltrationPrompt,
                    keywordForPrompt: keywordForPrompt?.text || null,
                    is_default: false,
                    keywordIds: keywordForPrompt ? [keywordForPrompt.id] : [],
                    isAiFilteringEnabled: isFilteringEnabled,
                  };
                  const response = await axios.post(`${API_BASE_URL}/prompts`, promptData, axiosConfig);
                  promptId = response.data.data.id;
                } else {
                  const selectedPromptObj = availablePrompts.find((p) => p.id === selectedPrompt);

                  if (selectedPromptObj) {
                    const promptData = {
                      name: selectedPromptObj.name,
                      model: selectedModel,
                      content: selectedPromptObj.content,
                      fillterationPrompt: constructedFiltrationPrompt,
                      keywordForPrompt: keywordForPrompt?.text || null,
                      is_default: selectedPromptObj.name === "Default",
                      keywordIds: keywordForPrompt ? [keywordForPrompt.id] : [],
                      isAiFilteringEnabled: isFilteringEnabled,
                    };
                    const response = await axios.post(`${API_BASE_URL}/prompts`, promptData, axiosConfig);
                    promptId = response.data.data.id;
                  }
                }

                await fetchAllPrompts();
                setPromptManagementOpen(false);
                setKeywordForPrompt(null);
                setNotification({
                  open: true,
                  message: keywordForPrompt
                    ? `Prompt for keyword "${keywordForPrompt.text}" saved successfully`
                    : "Prompt settings saved successfully",
                  severity: "success",
                });
              } catch (error) {
                console.error("Error saving prompt:", error);
                const isAuthError =
                  error.response?.status === 401 ||
                  error.response?.data?.message.toLowerCase().includes("token");

                if (isAuthError) {
                  setIsFilteringEnabled(false);
                  setUpgradeDialogOpen(true);
                } else {
                  const errorMessage = error.response?.data?.message || "Failed to save prompt settings";
                  setNotification({
                    open: true,
                    message: errorMessage,
                    severity: "error",
                  });
                }
              }
            }}
            variant="contained"
            sx={{ backgroundColor: "#4D99A3" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>AI Filtering Disabled</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your AI model connection has expired or is invalid. For your convenience, we've disabled the AI post filtering
            feature.
            <br />
            <br />
            Please upgrade to a premium plan to reactivate this feature and ensure uninterrupted access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpgradeDialogOpen(false)}>Close</Button>
          <Button href="/pricing" variant="contained" sx={{ backgroundColor: "#4D99A3" }}>
            Upgrade to Premium
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ top: 72 }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LinkedInKeywords;